import React, { useCallback, useEffect, useRef } from "react";
import utils from "../../app/UtilityService";
import containerUtils, { resolveRegions } from "../../utils/containerUtils";
import {
    Modal,
    ModalBody,
    ModalCancel,
    ModalFooter,
    ModalHeader,
    ModalSubmit
} from "@tinqin/tinqin-ui-components/src/modal";

export const ReduxModal = (props) => {
    const prevModalRef = useRef();
    // remove body overflow style on browser's back button
    const onPopState = useCallback(() => {
        document.body.style.overflow = "";
        window.removeEventListener("popstate", this.onPopState);
    }, []);

    const buildFooter = useCallback(
        (cancel, submit, footerContent) => {
            if (!cancel && !submit && !footerContent) {
                return null;
            }
            let footerConfig = props.config.footer;
            let footerProps = containerUtils.extractComponentProps(
                { config: footerConfig, data: props.data, widgetId: props.widgetId },
                { componentProps: ModalFooter.propTypes }
            );

            return (
                <ModalFooter {...footerProps}>
                    {footerContent}
                    {cancel && <ModalCancel>{cancel[0]}</ModalCancel>}
                    {submit && <ModalSubmit>{submit[0]}</ModalSubmit>}
                </ModalFooter>
            );
        },
        [props]
    );

    const buildBody = useCallback(
        (children) => {
            let bodyProps = containerUtils.extractComponentProps(
                { config: props.config.body, widgetId: props.widgetId },
                {
                    componentProps: ModalBody.propTypes,
                    skipDataTqId: true
                }
            );
            return <ModalBody {...bodyProps}>{children}</ModalBody>;
        },
        [props]
    );

    const buildHeading = useCallback(
        (header) => {
            let headingConfig = props.config.header;
            let headerProps = containerUtils.extractComponentProps(
                { config: headingConfig, data: props.data, widgetId: props.widgetId },
                { componentProps: ModalHeader.propTypes }
            );

            if (header && header[0]) {
                return <ModalHeader {...headerProps}>{header[0]}</ModalHeader>;
            } else {
                return <ModalHeader {...headerProps} />;
            }
        },
        [props]
    );

    const closeModal = useCallback(() => {
        props.clearGlobalModalState();
        props.clearUiStateDeep(props.widgetId, props.computedBindTo);
    }, [props]);

    useEffect(() => {
        let uiState = props.uiState || {};
        let prevUiState = (prevModalRef.current && prevModalRef.current.uiState) || {};
        let visible = !!uiState.visible;
        let prevVisible = !!prevUiState.visible;
        let modalProps = containerUtils.extractComponentProps(props, {
            additionalProps: ["disablePageScroll", "clearOnClose"],
            propsDefaults: {
                disablePageScroll: false,
                clearOnClose: false
            }
        });

        //Whenever change the modal visible state we need to deal with document scroll
        //Note! - this assumes that opened modal was rendered only due to a change of the store
        //Note! - this means that initially opened modals must be opened with action that changes the store
        //Note! - for backwards compatibility disablePageScroll is false by default
        const disablePageScroll = !!modalProps.disablePageScroll;
        if (visible !== prevVisible && disablePageScroll) {
            if (visible) {
                document.body.style.overflow = "hidden";
                window.addEventListener("popstate", onPopState);
            } else {
                document.body.style.overflow = "";
                window.removeEventListener("popstate", onPopState);
            }
        }
        prevModalRef.current = props;
        if (modalProps.clearOnClose) {
            let cachedDataPath = [props.widgetId, "cachedModal"];
            let truePath = props.truePath.slice();
            let data = props.data;
            if (visible) {
                //open
                props.compareCachedModalData(cachedDataPath, data);
            } else if (prevVisible) {
                //close
                truePath.pop();
                truePath.unshift(props.widgetId);
                props.triggerCopy(cachedDataPath, truePath);
                props.clearUiStateDeep(props.widgetId, props.computedBindTo);
            }
        }
    });

    useEffect(() => {
        const modalProps = containerUtils.extractComponentProps(props, {
            additionalProps: ["disablePageScroll"],
            propsDefaults: {
                disablePageScroll: false
            }
        });
        const disablePageScroll = !!modalProps.disablePageScroll;
        return function cleanup() {
            // remove overflow: hidden, when destroying component
            if (disablePageScroll && document.body.style.overflow === "hidden") {
                document.body.style.overflow = "";
                window.removeEventListener("popstate", onPopState);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let config = props.config;
    let resolvedModalBody;
    let resolvedModalFooter;
    let resolvedModalHeader;
    let visible = props.uiState && props.uiState.visible;
    let modal = null;
    let regions = resolveRegions(props);
    let modalProps = containerUtils.extractComponentProps(
        { config: config, widgetId: props.widgetId },
        {
            componentProps: Modal.propTypes
        }
    );

    modalProps.onClose = closeModal;
    //Proposal: to be removed. It seems to be undefined always
    // modalProps.onSubmit = onSubmit;
    modalProps.visible = visible;

    if (props.data && props.data.entities) {
        resolvedModalBody = buildBody(regions.body);
        resolvedModalFooter = buildFooter(regions.cancel, regions.submit, regions.footerContent);
        resolvedModalHeader = buildHeading(regions.header);
        modal = (
            <Modal {...modalProps}>
                {resolvedModalHeader}
                {resolvedModalBody}
                {resolvedModalFooter}
            </Modal>
        );
    }

    return modal;
};

ReduxModal.displayName = "Modal";

export default utils.connectComponent({
    actions: {
        clearGlobalModalState: "modal.clearGlobalModalState",
        compareCachedModalData: "modal.compareCachedModalData",
        triggerSet: "component.triggerSet",
        triggerCopy: "component.triggerCopy",
        clearUiStateDeep: "component.clearUiStateDeep"
    },
    uiState: true
})(ReduxModal);
